import moment from 'moment'

import { Booking } from '../../models/Booking'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Order } from '../../models/Order'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { Reader } from '../../models/Reader'
import { RecordFactory } from '../../models/RecordFactory'
import { Subscription } from '../../models/Subscription'
import { Transaction } from '../../models/Transaction'
import { Result } from '../../types/utils'
import { APP_VERSION } from '../../utils/constants'

// note: this is necessary because the surcharge on the package is only
// calculated when there is an attached source, so we don't want that restriction here
export const calculatePackageSurcharge = (pkg: Package) => {
    if (pkg.allowSurcharge && !pkg.markPaidDate) {
        return (
            (pkg.taxAmount + (pkg.amount ?? 0) + pkg.gratuityAmount) *
            Payment.PAYMENT_SURCHARGE_RATE
        )
    }
    return 0
}

export const sendChargeToReader = async (
    payment: Payment,
    reader: Reader
): Promise<Result<{ intent: any; intentId: string }>> => {
    const result = await RecordFactory.getInstance().createIntent(
        payment,
        reader.serialNumber ?? '',
        APP_VERSION,
        undefined,
        reader.reader,
        undefined,
        undefined,
        true
    )

    if (!result.ok) {
        return result
    }

    return {
        ok: true,
        value: {
            intent: result.value.intent,
            intentId: result.value.stripeIntentId,
        },
    }
}

/**
 * Marks a payment and its associated transaction as paid. This is used for situations where a payment is processed by the pro
 * on premises, such as cash or a mobile payment app like venmo.
 *
 * @param payment - The payment to send
 * @param trandoc - The initial transaction object for which a payment is being sent - e.g. a booking, estimate, order, etc.
 *
 * @returns The result of the mark paid transaction
 */
export const sendMarkPaid = async (payment: Payment, trandoc: Transaction | undefined) => {
    if (trandoc instanceof Booking) {
        const booking = trandoc.clone({
            source: payment.source,
            refnum: payment.refnum,
            mark_paid_date: payment.markPaidDate ?? moment(),
            gratuity: payment.gratuity,
            gratuity_type: payment.gratuityType,
            discount: payment.discount,
            discount_type: payment.discountType,
        })
        return await RecordFactory.getInstance().markBooking(booking, payment.sendReceipt)
    }
    else if (trandoc instanceof Estimate) {
        return await RecordFactory.getInstance().markEstimate(
            trandoc,
            payment.refnum,
            payment.paymeth,
            payment.gratuity,
            payment.gratuityType,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else if (trandoc instanceof Gift) {
        return await RecordFactory.getInstance().completeGift(
            trandoc,
            payment.refnum,
            payment.paymeth,
            payment.gratuity,
            payment.gratuityType,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else if (trandoc instanceof Package) {
        return await RecordFactory.getInstance().completePackage(
            trandoc,
            payment.refnum,
            payment.paymeth,
            payment.gratuity,
            payment.gratuityType,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else if (trandoc instanceof Invoice) {
        return await RecordFactory.getInstance().completeInvoice(
            trandoc,
            payment.refnum,
            payment.paymeth,
            payment.gratuity,
            payment.gratuityType,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else if (trandoc instanceof Package) {
        return await RecordFactory.getInstance().completePackage(
            trandoc,
            payment.refnum,
            payment.paymeth,
            payment.gratuity,
            payment.gratuityType,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else if (trandoc instanceof Invoice) {
        return await RecordFactory.getInstance().completeInvoice(
            trandoc,
            payment.refnum,
            payment.paymeth,
            payment.gratuity,
            payment.gratuityType,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else if (trandoc instanceof Order) {
        const order = trandoc.clone({
            source: payment.source,
            ship: payment.ship,
            ship_pricing: payment.shipPricing,
            ship_taxable: payment.shipTaxable,
            carrier: payment.carrier,
            delivery: payment.delivery,
            order: payment.shipment,
            allow_surcharge: payment.allowSurcharge,
            discount: payment.discount,
            discount_type: payment.discountType,
            tax: payment.tax,
            taxcode: payment.taxcode,
            nexus: payment.nexus,
            memo: payment.memo,
            rate: payment.total,
            lines: payment.lines,
        })
        return await RecordFactory.getInstance().markOrder(
            order,
            payment.refnum,
            payment.paymeth,
            payment.markPaidDate ?? moment(),
            payment.sendReceipt
        )
    }
    else {
        return await RecordFactory.getInstance().markCharge(payment)
    }
}

/**
 * Sends a card payment to the backend, which in turn will be processed by our payment provider.
 *
 * @param payment - The payment to send
 * @param trandoc - The transaction object for which a payment is being sent - e.g. a booking, estimate, order, etc.
 *
 * @returns The result of the card payment transaction
 */
export const submitCharge = async (payment: Payment, trandoc: Transaction | undefined) => {
    if (trandoc instanceof Booking && payment.type === 'deposit') {
        trandoc.source = payment.source
        trandoc.gratuity = payment.gratuity
        trandoc.gratuityType = payment.gratuityType

        return await RecordFactory.getInstance().confirmBooking(trandoc, true, true, payment.intent)
    }
    else if (trandoc instanceof Booking && payment.type === 'complete') {
        trandoc.lines = payment.lines.filter((line) => Boolean(line.item))
        trandoc.rate = (payment.total ?? 0) - (payment.paidDeposit ?? 0)
        trandoc.tax = payment.tax
        trandoc.taxcode = payment.taxcode
        if (trandoc.taxcode && !trandoc.nexus) {
            trandoc.nexus = 'pro'
        }
        trandoc.discount = payment.discount
        trandoc.discountType = payment.discountType
        trandoc.allowSurcharge = payment.allowSurcharge

        return await RecordFactory.getInstance().chargeBooking(
            trandoc,
            true,
            payment.source,
            payment.intent,
            payment.gratuity,
            payment.gratuityType
        )
    }
    else if (!!payment.order && payment.type === 'complete' && trandoc instanceof Order) {
        trandoc.source = payment.source
        trandoc.ship = payment.ship
        trandoc.shipPricing = payment.shipPricing
        trandoc.shipTaxable = payment.shipTaxable
        trandoc.carrier = payment.carrier
        trandoc.delivery = payment.delivery
        trandoc.order = payment.shipment
        trandoc.allowSurcharge = payment.allowSurcharge
        trandoc.discount = payment.discount
        trandoc.discountType = payment.discountType
        trandoc.tax = payment.tax
        trandoc.taxcode = payment.taxcode
        trandoc.nexus = payment.nexus
        trandoc.memo = payment.memo
        trandoc.rate = payment.total
        trandoc.lines = payment.lines

        return await RecordFactory.getInstance().chargeOrder(
            trandoc,
            true,
            payment.source,
            payment.intent
        )
    }
    else if (trandoc instanceof Estimate && payment.type === 'deposit') {
        trandoc.source = payment.source
        trandoc.gratuity = payment.gratuity
        trandoc.gratuityType = payment.gratuityType

        return await RecordFactory.getInstance().confirmEstimate(trandoc, true, payment.intent)
    }
    else if (trandoc instanceof Estimate && payment.type === 'complete') {
        return await RecordFactory.getInstance().chargeEstimate(
            trandoc,
            true,
            payment.source,
            payment.intent,
            payment.gratuity,
            payment.gratuityType
        )
    }
    else if (trandoc instanceof Subscription) {
        trandoc.source = payment.source
        trandoc.gratuity = payment.gratuity
        trandoc.gratuityType = payment.gratuityType
        return await RecordFactory.getInstance().confirmSubscription(trandoc, true)
    }
    else if (trandoc) {
        return await RecordFactory.getInstance().sendPayment(trandoc, payment)
    }
    else if (payment.type === 'payment') {
        return await RecordFactory.getInstance().sendPayment(undefined, payment)
    }
    return await RecordFactory.getInstance().sendCharge(payment)
}
